import * as numeral from 'numeral';
import * as _ from 'underscore';

import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { UtilityService } from '../../../../core/utility/services/utility.service';
import { DownloadConfig } from '../../../analytics-core/interfaces/widget.interface';
import { EVENT_EMITTERS } from '../../../analytics/constants/analytics.constants';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { WidgetCore } from '../../interfaces/widget.core.interface';

export interface CTVNetwork {
	networkName: string;
	friendlyName: string;
	logoPath: string;
	impressions: number;
	clicks: number;
	isPlatform: string;
	percent25: number;
	percent50: number;
	percent75: number;
	percent100: number;
	completions: number;
	completionRate: number;
	displayCompletionRate: string;
}

@Component({
	selector: 'app-ctv-featured-networks',
	styleUrls: ['./ctv-featured-networks.component.css'],
	templateUrl: './ctv-featured-networks.component.html'
})
export class CtvFeaturedNetworksComponent implements OnInit, WidgetCore, OnDestroy {
	@Input() downloadconfig: any;
	@Input() sectionclass: any;
	@Input() defaultsort: string;
	@Input() impressionsviewedcompletionsrate: string;
	@Input() nodatamessage;
	@Input() downloadcsvcolumns: any;

	public key: string;
	public sectiontitle: string;
	showLoad = false;
	public hasData: boolean;
	public featuredNetworks: CTVNetwork[] = [];
	public reloadSubscription;
	public widgetDataSubscription;
	public p2: any;
	public downloadConfiguration: DownloadConfig;
	public itemsPerPage = 12;
	public breakNumber = 6;
	constructor(protected analyticsService: AnalyticsService) {
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe((response: any) => {
			this.p2 = 1;
			this.requestData();
		});
	}

	ngOnInit(): void {
		this.downloadConfiguration = JSON.parse(decodeURI(this.downloadconfig));
		if (this.defaultsort === 'true') {
			this.defaultsort = JSON.parse(this.defaultsort);
		} else {
			this.defaultsort = null;
		}
		if (this.sectionclass === 'cox_ctv_featured_networks' || this.sectionclass === 'r6-featured-networks') {
			this.itemsPerPage = 10;
			this.breakNumber = 5;
		}
		if (!this.nodatamessage || this.nodatamessage === 'undefined') {
			this.nodatamessage = 'No data to display';
		}
		this.downloadcsvcolumns = this.downloadcsvcolumns ? JSON.parse(decodeURI(this.downloadcsvcolumns)) : [];
		this.requestData();
	}

	ngOnDestroy(): void {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
	}

	requestData(): void {
		this.hasData = true;
		this.showLoad = true;
		this.widgetDataSubscription = this.analyticsService.processWidgetRequest(this.key).subscribe((response: any) => {
			this.showLoad = false;
			if (response.success && response.data && response.data.length > 0) {
				this.hasData = true;
				this.processResponseData(response.data);
			} else {
				this.hasData = false;
			}
		});
	}

	processResponseData(response: any): void {
		this.featuredNetworks = this.mapCTVNetworkResponseToInterface(response);
		if (!this.defaultsort) {
			this.featuredNetworks = _.sortBy(this.featuredNetworks, 'impressions').reverse();
		}
		this.featuredNetworks['events'] = {
			load: (): void => {
				const graph = this['series'][0].data[1].graphic;
				setTimeout(() => {
					const prev = graph.r;
					graph.attr({
						innerR: 55,
						r: prev - 1
					});
				}, 700);
			}
		};
	}

	mapCTVNetworkResponseToInterface(responseArray: any[]): CTVNetwork[] {
		const ctvNetworks: CTVNetwork[] = [];
		for (const responseObj of responseArray) {
			const ctvNetwork: CTVNetwork = {} as CTVNetwork;
			ctvNetwork.networkName = !UtilityService.isUndefinedOrNull(responseObj.network_name)
				? responseObj.network_name
				: responseObj.name;
			ctvNetwork.friendlyName = responseObj.friendly_name;
			ctvNetwork.logoPath = responseObj.logo_path;
			ctvNetwork.isPlatform = responseObj.is_platform;
			ctvNetwork.impressions = !UtilityService.isUndefinedOrNull(responseObj.impressions)
				? parseInt(responseObj.impressions, 10)
				: 0;
			ctvNetwork.completions = !UtilityService.isUndefinedOrNull(responseObj.completions)
				? parseInt(responseObj.completions, 10)
				: 0;
			if (this.impressionsviewedcompletionsrate === 'true') {
				ctvNetwork.completionRate = UtilityService.isUndefinedOrNull(responseObj.impressions_viewed_completions_rate)
					? parseFloat(responseObj['100_completions_rate'])
					: responseObj.impressions_viewed_completions_rate;
			} else {
				ctvNetwork.completionRate = UtilityService.isUndefinedOrNull(responseObj.completions_rate)
					? parseFloat(responseObj['100_completions_rate'])
					: responseObj.completions_rate;
			}
			ctvNetwork.displayCompletionRate = !UtilityService.isUndefinedOrNull(responseObj.completions_rate)
				? numeral(responseObj.completions_rate).format('0,0.00')
				: 0;
			ctvNetwork.percent25 = !UtilityService.isUndefinedOrNull(responseObj['25_percent'])
				? parseInt(responseObj['25_percent'], 10)
				: 0;
			ctvNetwork.percent50 = !UtilityService.isUndefinedOrNull(responseObj['50_percent'])
				? parseInt(responseObj['50_percent'], 10)
				: 0;
			ctvNetwork.percent75 = !UtilityService.isUndefinedOrNull(responseObj['75_percent'])
				? parseInt(responseObj['75_percent'], 10)
				: 0;
			ctvNetwork.percent100 = !UtilityService.isUndefinedOrNull(responseObj['100_percent'])
				? parseInt(responseObj['100_percent'], 10)
				: 0;
			ctvNetworks.push(ctvNetwork);
		}
		_.sortBy(ctvNetworks, (ctvNetwork) => {
			return ctvNetwork.completionRate;
		});
		return ctvNetworks;
	}

	downloadDataInCSV(event: any): void {
		event.preventDefault();
		this.analyticsService.downloadCSV(
			this.downloadConfiguration.csvQueryKey,
			this.downloadConfiguration.fileName,
			this.downloadcsvcolumns
		);
	}
}
